
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export type PrayerPoint = string;

export type ActionStep = string;

export interface ReadingPlanDay {
  day: string;
  passage: string;
  scripture?: string; // Added scripture text field
  commentary: string;
  actionSteps: ActionStep[];
  prayerPoints: PrayerPoint[];
}

export interface ReadingPlanData {
  readingPlan: ReadingPlanDay[];
}

export const useReadingPlan = () => {
  const [topic, setTopic] = useState<string>('');
  const [days, setDays] = useState<number>(7);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [readingPlan, setReadingPlan] = useState<ReadingPlanData | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!topic.trim()) {
      toast({
        title: "Topic Required",
        description: "Please enter a topic for your reading plan.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setReadingPlan(null);

    try {
      // Add a retry mechanism for API calls
      let attempts = 0;
      const maxAttempts = 2;
      let success = false;
      let data;
      let error;

      while (attempts < maxAttempts && !success) {
        attempts++;
        try {
          const response = await supabase.functions.invoke('reading-plan', {
            body: { topic, days },
          });
          
          if (response.error) {
            throw new Error(response.error.message);
          }
          
          data = response.data;
          
          if (data?.error) {
            throw new Error(data.error);
          }
          
          if (!data?.readingPlan || !Array.isArray(data.readingPlan)) {
            throw new Error("Invalid response format from the server");
          }
          
          success = true;
        } catch (e) {
          error = e;
          // Wait briefly before retrying
          if (attempts < maxAttempts) {
            await new Promise(r => setTimeout(r, 1000));
          }
        }
      }

      if (!success) {
        throw error || new Error("Failed to generate reading plan after multiple attempts");
      }

      setReadingPlan(data);
      toast({
        title: "Reading Plan Generated",
        description: `Your ${days}-day reading plan on "${topic}" is ready!`,
      });
    } catch (error) {
      console.error('Error generating reading plan:', error);
      toast({
        title: "Error",
        description: "Failed to generate reading plan. Please try a different topic or try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveReadingPlan = async () => {
    if (!readingPlan) {
      toast({
        title: "No Reading Plan",
        description: "There is no reading plan to save.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to save reading plans.",
          variant: "destructive",
        });
        return;
      }

      // Create a plain JSON object for the content
      const contentObj = {
        topic,
        days,
        readingPlan: readingPlan.readingPlan as any[], // Cast to any[] to bypass TypeScript check
        createdAt: new Date().toISOString()
      };

      const { error } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'reading_plan',
          title: `Reading Plan: ${topic}`,
          content: contentObj,
          user_id: session.session.user.id
        });

      if (error) {
        throw error;
      }

      toast({
        title: "Reading Plan Saved",
        description: "Your reading plan has been saved to your dashboard.",
      });
    } catch (error) {
      console.error('Error saving reading plan:', error);
      toast({
        title: "Error",
        description: "Failed to save reading plan. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    topic,
    setTopic,
    days,
    setDays,
    isLoading,
    isSaving,
    readingPlan,
    handleSubmit,
    saveReadingPlan,
  };
};

export default useReadingPlan;
